<script setup>
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { onClickOutside, useElementBounding } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';
import DashboardEasyDataTable from 'dashboard/components/table/DashboardEasyDataTable.vue';
import DashboardEasyDataTableFooter from 'dashboard/components/table/DashboardEasyDataTableFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import FormInput from 'v3/components/Form/Input.vue';
import { getHelpUrlForFeature } from 'dashboard/helper/featureHelper';
import { messageStamp } from 'shared/helpers/timeHelper';

/** Descriptions longer than this get Show more / Show less (single-line when collapsed). */
const DESCRIPTION_TOGGLE_MIN_CHARS = 32;

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();
const confirmDialog = ref(null);

const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const addMenuOpen = ref(false);
const moreMenuOpen = ref(false);
const openRowMenuId = ref(null);
const rowMenuTriggerEl = shallowRef(null);
const rowMenuDropdownEl = shallowRef(null);
const ROW_MENU_WIDTH = 224;
const ROW_MENU_GAP = 4;
const rowMenuLayoutTick = ref(0);
const dropdownBb = useElementBounding(rowMenuDropdownEl, {
  windowResize: true,
  windowScroll: true,
});

const expandedDescriptionById = ref({});

const isDescriptionExpanded = id => Boolean(expandedDescriptionById.value[id]);

const toggleDescriptionExpanded = id => {
  expandedDescriptionById.value = {
    ...expandedDescriptionById.value,
    [id]: !expandedDescriptionById.value[id],
  };
};

const shouldOfferDescriptionToggle = description => {
  const text = String(description ?? '').trim();
  return text.length > DESCRIPTION_TOGGLE_MIN_CHARS;
};

const workflowsTableRef = ref(null);

watch(openRowMenuId, async (id, _old, onCleanup) => {
  if (!id) {
    rowMenuTriggerEl.value = null;
    return;
  }
  await nextTick();
  const dd = rowMenuDropdownEl.value;
  if (dd) {
    const stop = onClickOutside(
      dd,
      () => {
        openRowMenuId.value = null;
        rowMenuTriggerEl.value = null;
      },
      { ignore: [rowMenuTriggerEl] }
    );
    onCleanup?.(() => stop());
  }
  const table = workflowsTableRef.value?.getTableRef?.();
  const scrollEl = table?.$el?.querySelector?.('.vue3-easy-data-table__main');
  const bump = () => {
    rowMenuLayoutTick.value += 1;
    dropdownBb.update();
  };
  if (scrollEl) {
    scrollEl.addEventListener('scroll', bump, { passive: true });
    onCleanup?.(() => scrollEl.removeEventListener('scroll', bump));
  }
  window.addEventListener('scroll', bump, true);
  onCleanup?.(() => window.removeEventListener('scroll', bump, true));
  bump();
});

const metaDialogRef = ref(null);
const metaEditAutomation = ref(null);
const draftName = ref('');
const draftDescription = ref('');
const modalNameError = ref('');
const modalDescError = ref('');

const records = computed(() => getters['automations/getAutomations'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});
const uiFlags = computed(() => getters['automations/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);

const helpUrl = computed(() => getHelpUrlForFeature('automation'));

const notAvailable = computed(() =>
  t('AUTOMATION.WORKFLOWS_TABLE.NOT_AVAILABLE')
);

const formatAgentDisplay = user => {
  if (!user || typeof user !== 'object') return notAvailable.value;
  return user.available_name || user.name || notAvailable.value;
};

const formatWorkflowUnixDate = unix => {
  if (unix == null || unix === '') return notAvailable.value;
  return messageStamp(Number(unix), 'LLL d, yyyy');
};

/** Full date + time for native tooltip on hover (undefined when no timestamp). */
const formatWorkflowUnixTitle = unix => {
  if (unix == null || unix === '') return undefined;
  return messageStamp(Number(unix), 'LLL d, yyyy, h:mm a');
};

const tableItems = computed(() =>
  filteredRecords.value.map(a => ({
    id: a.id,
    name: a.name,
    active: Boolean(a.active),
    lastPublishedBy: formatAgentDisplay(
      a.last_published_by ?? a.lastPublishedBy
    ),
    lastPublishedOn: a.updated_on ?? a.created_on,
    createdBy: formatAgentDisplay(a.created_by ?? a.createdBy),
    createdOn: a.created_on,
    actions: a.id,
    raw: a,
  }))
);

const openRowMenuItem = computed(() => {
  const id = openRowMenuId.value;
  if (id == null) return null;
  return tableItems.value.find(i => i.id === id) ?? null;
});

const rowMenuFloatingStyles = computed(() => {
  const el = rowMenuTriggerEl.value;
  if (!openRowMenuId.value || !el?.getBoundingClientRect) {
    return {};
  }

  const r = el.getBoundingClientRect();
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  const mw = ROW_MENU_WIDTH;
  const measuredH = dropdownBb.height.value;
  const mh = measuredH > 80 ? Math.min(measuredH, 560) : 280;
  const gap = ROW_MENU_GAP;

  const dir =
    typeof getComputedStyle === 'function'
      ? getComputedStyle(el).direction
      : 'ltr';
  const rtl = dir === 'rtl';

  let left = rtl ? r.left : r.right - mw;
  left = Math.max(gap, Math.min(left, vw - mw - gap));

  const spaceBelow = vh - r.bottom - gap + rowMenuLayoutTick.value * 0;
  const spaceAbove = r.top - gap;
  const openAbove = spaceBelow < mh && spaceAbove > spaceBelow;

  let top = openAbove ? r.top - mh - gap : r.bottom + gap;
  top = Math.max(gap, Math.min(top, vh - mh - gap));

  return {
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${ROW_MENU_WIDTH}px`,
    zIndex: 10000,
  };
});

const workflowTableHeaders = computed(() => [
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.STATUS'),
    value: 'active',
    sortable: true,
    width: 128,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.NAME'),
    value: 'name',
    sortable: true,
    width: 224,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.LAST_PUBLISHED_BY'),
    value: 'lastPublishedBy',
    sortable: true,
    width: 160,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.LAST_PUBLISHED'),
    value: 'lastPublishedOn',
    sortable: true,
    width: 200,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.CREATED_BY'),
    value: 'createdBy',
    sortable: true,
    width: 160,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.DATE_CREATED'),
    value: 'createdOn',
    sortable: true,
    width: 200,
  },
  {
    text: t('AUTOMATION.WORKFLOWS_TABLE.COLUMNS.ACTIONS'),
    value: 'actions',
    sortable: true,
    width: 72,
  },
]);

const addMenuItems = computed(() => [
  {
    action: 'create',
    value: 'new',
    label: t('AUTOMATION.WORKFLOWS_PAGE.NEW_WORKFLOW'),
    icon: 'i-lucide-plus',
  },
]);

const moreMenuItems = computed(() => {
  const items = [];
  if (helpUrl.value) {
    items.push({
      action: 'help',
      value: 'docs',
      label: t('AUTOMATION.LEARN_MORE'),
      icon: 'i-lucide-book-open',
    });
  }
  return items;
});

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);

const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);

const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  store.dispatch('automations/get');
  if (isSLAEnabled.value) {
    store.dispatch('sla/get');
  }
});

const openAddEditor = () => {
  addMenuOpen.value = false;
  router.push({
    name: 'automation_editor_new',
    params: { accountId: accountId.value },
  });
};

const onAddMenuAction = ({ action }) => {
  addMenuOpen.value = false;
  if (action === 'create') {
    openAddEditor();
  }
};

const onMoreMenuAction = ({ action }) => {
  moreMenuOpen.value = false;
  if (action === 'help' && helpUrl.value) {
    window.open(helpUrl.value, '_blank', 'noopener,noreferrer');
  }
};

const openEditEditor = response => {
  router.push({
    name: 'automation_editor_edit',
    params: { accountId: accountId.value, automationId: response.id },
  });
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAutomation.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAutomation = async id => {
  try {
    await store.dispatch('automations/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};
const confirmDeletion = () => {
  loading.value[selectedAutomation.value.id] = true;
  closeDeletePopup();
  deleteAutomation(selectedAutomation.value.id);
};

const cloneAutomation = async automation => {
  const cloneId = automation.id;
  try {
    loading.value[cloneId] = true;
    await store.dispatch('automations/clone', cloneId);
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('automations/get');
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[cloneId] = false;
  }
};

const toggleAutomation = async ({ id, name, status }) => {
  try {
    if (status) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    }

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automations/update', {
        id: id,
        active: !status,
      });
      const message = status
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const toggleRowMenu = (item, event) => {
  if (openRowMenuId.value === item.id) {
    openRowMenuId.value = null;
    rowMenuTriggerEl.value = null;
    return;
  }
  const fromPoint =
    typeof document !== 'undefined' &&
    event?.clientX != null &&
    event?.clientY != null
      ? document
          .elementFromPoint(event.clientX, event.clientY)
          ?.closest('button')
      : null;
  const anchor =
    (event?.currentTarget instanceof HTMLButtonElement
      ? event.currentTarget
      : null) ??
    fromPoint ??
    (event?.target instanceof Element ? event.target.closest('button') : null);
  if (!anchor) {
    return;
  }
  rowMenuTriggerEl.value = anchor;
  openRowMenuId.value = item.id;
  nextTick(() => {
    rowMenuLayoutTick.value += 1;
    dropdownBb.update();
    requestAnimationFrame(() => {
      rowMenuLayoutTick.value += 1;
      dropdownBb.update();
    });
  });
};

const rowMenuItems = item => {
  const raw = item.raw;
  const publishStop = raw.active
    ? {
        action: 'toggle',
        value: 'toggle',
        label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.STOP'),
        icon: 'i-lucide-circle-stop',
      }
    : {
        action: 'toggle',
        value: 'toggle',
        label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.PUBLISH'),
        icon: 'i-lucide-circle-play',
      };

  return [
    publishStop,
    {
      action: 'editMeta',
      value: 'editMeta',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.EDIT_META'),
      icon: 'i-lucide-text-cursor-input',
    },
    {
      action: 'builder',
      value: 'builder',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.OPEN_BUILDER'),
      icon: 'i-lucide-workflow',
    },
    {
      action: 'clone',
      value: 'clone',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.CLONE'),
      icon: 'i-lucide-copy',
    },
    {
      action: 'export',
      value: 'export',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.EXPORT'),
      icon: 'i-lucide-download',
    },
    {
      action: 'settings',
      value: 'settings',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.SETTINGS'),
      icon: 'i-lucide-settings',
    },
    {
      action: 'delete',
      value: 'delete',
      label: t('AUTOMATION.WORKFLOWS_TABLE.ROW_MENU.DELETE'),
      icon: 'i-lucide-trash-2',
    },
  ];
};

const exportWorkflow = automation => {
  const payload = {
    id: automation.id,
    name: automation.name,
    description: automation.description,
    event_name: automation.event_name,
    conditions: automation.conditions,
    actions: automation.actions,
    active: automation.active,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = String(automation.name || 'workflow')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
  a.href = url;
  a.download = `workflow-${automation.id}-${safe}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const openMetaModal = automation => {
  metaEditAutomation.value = automation;
  draftName.value = automation.name || '';
  draftDescription.value = automation.description || '';
  modalNameError.value = '';
  modalDescError.value = '';
  metaDialogRef.value?.open();
};

const onMetaDialogClose = () => {
  modalNameError.value = '';
  modalDescError.value = '';
  metaEditAutomation.value = null;
};

const applyMetaFromModal = async () => {
  modalNameError.value = '';
  modalDescError.value = '';
  const name = String(draftName.value || '').trim();
  const desc = String(draftDescription.value || '').trim();
  if (!name) {
    modalNameError.value = t('AUTOMATION.ADD.FORM.NAME.ERROR');
  }
  if (!desc) {
    modalDescError.value = t('AUTOMATION.ADD.FORM.DESC.ERROR');
  }
  if (modalNameError.value || modalDescError.value) return;

  const automation = metaEditAutomation.value;
  if (!automation?.id) return;

  try {
    await store.dispatch('automations/update', {
      id: automation.id,
      name,
      description: desc,
    });
    useAlert(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
    metaDialogRef.value?.close();
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const onRowMenuAction = ({ action }, item) => {
  openRowMenuId.value = null;
  rowMenuTriggerEl.value = null;
  const raw = item.raw;
  if (action === 'toggle') {
    toggleAutomation({
      id: raw.id,
      name: raw.name,
      status: raw.active,
    });
  } else if (action === 'editMeta') {
    openMetaModal(raw);
  } else if (action === 'builder') {
    openEditEditor(raw);
  } else if (action === 'clone') {
    cloneAutomation(raw);
  } else if (action === 'export') {
    exportWorkflow(raw);
  } else if (action === 'settings') {
    openEditEditor(raw);
  } else if (action === 'delete') {
    openDeletePopup(raw);
  }
};
</script>

<template>
  <SettingsLayout
    class="flex h-full min-h-0 w-full min-w-0 flex-1 basis-0 flex-col"
    section-gap-class="gap-0"
    main-content-class="overflow-hidden !px-0 md:!px-0"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION.LIST.404')"
  >
    <template #header>
      <header
        class="relative z-20 flex h-[49px] min-h-[49px] max-h-[49px] w-full shrink-0 flex-row items-center border-b border-n-weak px-3 py-0 dark:border-n-strong md:px-4"
      >
        <div
          class="flex h-full min-h-0 w-full min-w-0 flex-nowrap items-center justify-between gap-2"
        >
          <div
            class="flex min-h-0 min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto sm:gap-3"
          >
            <h1
              class="m-0 shrink-0 text-lg font-bold leading-tight text-n-slate-12"
            >
              {{ $t('AUTOMATION.WORKFLOWS_PAGE.TITLE') }}
            </h1>
            <div
              class="w-full min-w-0 sm:w-64 sm:max-w-xs sm:flex-1 sm:basis-64"
            >
              <Input
                v-model="searchQuery"
                :placeholder="
                  $t('AUTOMATION.WORKFLOWS_PAGE.SEARCH_PLACEHOLDER')
                "
                custom-input-class="!h-8 !min-h-8 !max-h-8 !py-0 !px-3"
                class="group w-full min-w-0 gap-0 [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
                size="sm"
                type="search"
              />
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-1.5">
            <OnClickOutside @trigger="addMenuOpen = false">
              <div class="relative flex items-stretch">
                <div
                  class="flex overflow-hidden rounded-lg shadow-sm outline outline-1 outline-n-brand -outline-offset-1"
                >
                  <Button
                    size="sm"
                    solid
                    blue
                    class="!rounded-none !rounded-l-lg !border-0 !pr-3"
                    :label="$t('AUTOMATION.WORKFLOWS_PAGE.ADD_WORKFLOW')"
                    @click="openAddEditor"
                  />
                  <div
                    class="w-px shrink-0 self-stretch bg-white/25"
                    aria-hidden="true"
                  />
                  <Button
                    icon="i-lucide-chevron-down"
                    size="sm"
                    solid
                    blue
                    class="!rounded-none !rounded-r-lg !border-0 !px-2"
                    :aria-label="$t('AUTOMATION.WORKFLOWS_PAGE.ADD_WORKFLOW')"
                    @click="addMenuOpen = !addMenuOpen"
                  />
                </div>
                <DropdownMenu
                  v-if="addMenuOpen"
                  :menu-items="addMenuItems"
                  class="absolute end-0 top-full z-[100] mt-1 w-52"
                  @action="onAddMenuAction"
                />
              </div>
            </OnClickOutside>

            <OnClickOutside @trigger="moreMenuOpen = false">
              <div class="relative">
                <Button
                  icon="i-lucide-more-horizontal"
                  size="sm"
                  faded
                  slate
                  class="!px-2"
                  :aria-label="$t('AUTOMATION.WORKFLOWS_PAGE.MORE_OPTIONS')"
                  @click="moreMenuOpen = !moreMenuOpen"
                />
                <DropdownMenu
                  v-if="moreMenuOpen && moreMenuItems.length"
                  :menu-items="moreMenuItems"
                  class="absolute end-0 top-full z-[100] mt-1 w-52"
                  @action="onMoreMenuAction"
                />
              </div>
            </OnClickOutside>
          </div>
        </div>
      </header>
    </template>
    <template #body>
      <div class="flex h-full min-h-0 min-w-0 flex-1 flex-col self-stretch">
        <DashboardEasyDataTable
          ref="workflowsTableRef"
          fill-available-height
          hide-built-in-footer
          :headers="workflowTableHeaders"
          :items="tableItems"
          :rows-per-page-message="
            $t('AUTOMATION.WORKFLOWS_TABLE.PAGINATION_ROWS_LABEL')
          "
          :pagination-reset-key="searchQuery.trim()"
          extra-table-class="workflow-easy-data-table"
          extra-header-class="workflows-table-head"
          :empty-message="$t('AUTOMATION.LIST.404')"
        >
          <template #empty-message>
            <span class="text-body-main text-n-slate-11">
              {{
                searchQuery.trim()
                  ? $t('AUTOMATION.NO_RESULTS')
                  : $t('AUTOMATION.LIST.404')
              }}
            </span>
          </template>

          <template #item-active="item">
            <span
              class="inline-flex max-w-full items-center rounded-md px-2 py-0.5 text-sm font-bold"
              :class="
                item.active
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{
                item.active
                  ? $t('AUTOMATION.WORKFLOWS_TABLE.STATUS_PUBLISHED')
                  : $t('AUTOMATION.WORKFLOWS_TABLE.STATUS_STOPPED')
              }}
            </span>
          </template>

          <template #item-name="item">
            <div
              class="flex min-w-0 w-full max-w-[14rem] flex-col gap-1 py-0.5"
            >
              <span
                class="truncate text-sm font-medium leading-snug text-n-brand"
              >
                {{ item.name }}
              </span>
              <template v-if="item.raw.description">
                <p
                  class="text-sm leading-snug text-n-slate-11"
                  :class="isDescriptionExpanded(item.id) ? '' : 'truncate'"
                >
                  {{ item.raw.description }}
                </p>
                <button
                  v-if="shouldOfferDescriptionToggle(item.raw.description)"
                  type="button"
                  class="w-fit rounded text-xs font-medium text-n-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
                  @click.stop="toggleDescriptionExpanded(item.id)"
                >
                  {{
                    isDescriptionExpanded(item.id)
                      ? $t('AUTOMATION.WORKFLOWS_TABLE.SHOW_LESS')
                      : $t('AUTOMATION.WORKFLOWS_TABLE.SHOW_MORE')
                  }}
                </button>
              </template>
            </div>
          </template>

          <template #item-lastPublishedBy="item">
            <span class="text-sm text-n-slate-11">
              {{ item.lastPublishedBy }}
            </span>
          </template>

          <template #item-lastPublishedOn="item">
            <span
              :title="formatWorkflowUnixTitle(item.lastPublishedOn)"
              class="whitespace-nowrap text-sm text-n-slate-12 tabular-nums"
            >
              {{ formatWorkflowUnixDate(item.lastPublishedOn) }}
            </span>
          </template>

          <template #item-createdBy="item">
            <span class="text-sm text-n-slate-11">
              {{ item.createdBy }}
            </span>
          </template>

          <template #item-createdOn="item">
            <span
              :title="formatWorkflowUnixTitle(item.createdOn)"
              class="whitespace-nowrap text-sm text-n-slate-12 tabular-nums"
            >
              {{ formatWorkflowUnixDate(item.createdOn) }}
            </span>
          </template>

          <template #item-actions="item">
            <div class="flex w-full justify-center">
              <Button
                icon="i-lucide-more-vertical"
                size="sm"
                faded
                slate
                class="!px-2"
                :aria-label="$t('AUTOMATION.WORKFLOWS_PAGE.MORE_OPTIONS')"
                :is-loading="Boolean(loading[item.id])"
                @click.stop="toggleRowMenu(item, $event)"
              />
            </div>
          </template>
        </DashboardEasyDataTable>

        <Teleport to="body">
          <DropdownMenu
            v-if="openRowMenuItem"
            ref="rowMenuDropdownEl"
            position-strategy="fixed"
            :floating-styles="rowMenuFloatingStyles"
            :menu-items="rowMenuItems(openRowMenuItem)"
            class="w-56"
            @action="payload => onRowMenuAction(payload, openRowMenuItem)"
          />
        </Teleport>

        <DashboardEasyDataTableFooter
          class="!px-0 md:!px-0"
          :wrapper-ref="workflowsTableRef"
          :item-count="tableItems.length"
          :rows-per-page-label="
            $t('AUTOMATION.WORKFLOWS_TABLE.PAGINATION_ROWS_LABEL')
          "
          :nav-aria-label="$t('AUTOMATION.WORKFLOWS_TABLE.PAGINATION_ARIA')"
        />
      </div>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />

    <Dialog
      ref="metaDialogRef"
      type="edit"
      :title="$t('AUTOMATION.EDITOR.META_MODAL_TITLE')"
      width="lg"
      :confirm-button-label="$t('AUTOMATION.EDITOR.META_SAVE')"
      :cancel-button-label="$t('AUTOMATION.FORM.CANCEL')"
      @confirm="applyMetaFromModal"
      @close="onMetaDialogClose"
    >
      <div class="flex w-full flex-col gap-4">
        <div class="flex flex-col gap-1">
          <FormInput
            v-model="draftName"
            name="automation-list-meta-name"
            spacing="compact"
            :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
            :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
            :has-error="Boolean(modalNameError)"
            :error-message="modalNameError"
          />
        </div>
        <TextArea
          id="automation-list-meta-description"
          v-model="draftDescription"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          :message="modalDescError"
          :message-type="modalDescError ? 'error' : 'info'"
          resize
          min-height="5rem"
        />
      </div>
    </Dialog>
  </SettingsLayout>
</template>
